import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { ActionBar, Modal, RolesIcon, Select, Text } from '@viapeople/components'
import { useMemo, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc, trpcClient } from '../../../../utils/trpc'
import type { CycleRolesUnmapped, CycleRolesUnmappedInput } from '../types'

const CycleUnmappedRoles = () => {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()
	const { currentStep, markStepCompleted } = useOutletContext<{
		currentStep: number
		markStepCompleted: (idx: number) => void
	}>()
	const [participantsRolesToMap, setParticipantsRolesToMap] = useState<
		{ id: string; roleId: string; formId: string }[]
	>([])
	const [bulkRoleId, setBulkRoleId] = useState<string>('')
	const [bulkFormId, setBulkFormId] = useState<string>('')
	const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false)

	const toast = useToast()

	const { data: roles } = useQuery({
		...trpc.admin.cycles.roles.list.queryOptions({
			cycleId: cycleId as string,
		}),
		enabled: !!cycleId,
	})

	const {
		data: formsData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ['forms', 'search', organizationId],
		queryFn: async ({ pageParam = 1 }) => {
			const result = await trpcClient.admin.forms.search.query({
				organizationId: organizationId as string,
				page: pageParam,
				perPage: 10,
			})
			return result
		},
		getNextPageParam: (lastPage) => {
			const { page, perPage, total } = lastPage.meta
			const hasMore = page * perPage < total
			return hasMore ? page + 1 : undefined
		},
		initialPageParam: 1,
		enabled: !!organizationId,
	})

	const formOptions = useMemo(() => {
		if (!formsData?.pages) return []
		return formsData.pages.flatMap((page) =>
			page.data.map((form: { name: string; id: string }) => ({
				label: form.name,
				value: form.id,
			})),
		)
	}, [formsData])

	const columns: Column<CycleRolesUnmapped>[] = [
		{
			field: 'firstName',
			label: 'First Name',
			colSpan: 2,
			render: (role: CycleRolesUnmapped) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.firstName}
				</Text>
			),
		},
		{
			field: 'lastName',
			label: 'Last Name',
			colSpan: 2,
			render: (role: CycleRolesUnmapped) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.lastName}
				</Text>
			),
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 2,
			render: (role: CycleRolesUnmapped) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.email}
				</Text>
			),
		},
		{
			field: 'id',
			label: 'ID',
			colSpan: 2,
			render: (role: CycleRolesUnmapped) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.id}
				</Text>
			),
		},
		{
			field: 'formId' as keyof CycleRolesUnmapped,
			label: 'Select Form',
			colSpan: 1,
			render: (roleUnmapped: CycleRolesUnmapped) => {
				if (!formOptions.length)
					return (
						<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
							No forms available
						</Text>
					)
				return (
					<Select
						key={`${roleUnmapped.id}-form`}
						value={participantsRolesToMap.find((r) => r.id === roleUnmapped.id)?.formId ?? ''}
						placeholder='Select form'
						options={formOptions}
						onChange={(value) => {
							const existing = participantsRolesToMap.find((r) => r.id === roleUnmapped.id)
							if (!existing) {
								setParticipantsRolesToMap((prev) => [...prev, { id: roleUnmapped.id, roleId: '', formId: value }])
							} else {
								setParticipantsRolesToMap((prev) =>
									prev.map((r) => (r.id === roleUnmapped.id ? { ...r, formId: value } : r)),
								)
							}
						}}
						onMenuScrollToBottom={() => {
							if (hasNextPage && !isFetchingNextPage) {
								fetchNextPage()
							}
						}}
						isLoadingMore={isFetchingNextPage}
						hasMore={hasNextPage}
					/>
				)
			},
		},
		{
			field: 'roleName',
			label: 'Role',
			colSpan: 1,
			render: (roleUnmapped: CycleRolesUnmapped) => {
				if (!roles)
					return (
						<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
							No roles available
						</Text>
					)
				return (
					<Select
						key={`${roleUnmapped.id}-role`}
						value={participantsRolesToMap.find((r) => r.id === roleUnmapped.id)?.roleId ?? ''}
						placeholder='Select role'
						options={
							roles?.data
								?.filter((value) => value.roleId !== null)
								.map((value) => ({
									label: value.name,
									value: value.roleId as string,
								})) ?? []
						}
						onChange={(value) => {
							const autoFormId = roles?.data?.find((r) => r.roleId === value)?.formId ?? ''
							const existing = participantsRolesToMap.find((r) => r.id === roleUnmapped.id)
							if (!existing) {
								setParticipantsRolesToMap((prev) => [
									...prev,
									{ id: roleUnmapped.id, roleId: value, formId: autoFormId },
								])
							} else {
								setParticipantsRolesToMap((prev) =>
									prev.map((r) => (r.id === roleUnmapped.id ? { ...r, roleId: value, formId: autoFormId } : r)),
								)
							}
						}}
					/>
				)
			},
		},
	]

	const assignRolesToParticipantsMutation = useMutation({
		...trpc.admin.cycles.roles.participants.assign.mutationOptions({
			onSuccess: () => {
				toast.showToast('Roles assigned')
			},
			onError: () => {
				toast.showToast('Error assigning roles', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.list.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const bulkAssignAllMutation = useMutation({
		...trpc.admin.cycles.roles.participants.bulkAssignAll.mutationOptions({
			onSuccess: (data) => {
				toast.showToast(`Successfully assigned role and form to ${data.count} participants`)
				setIsBulkAssignModalOpen(false)
				setBulkRoleId('')
				setBulkFormId('')
			},
			onError: () => {
				toast.showToast('Error performing bulk assignment', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.roles.list.queryKey({ cycleId: cycleId as string }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleBulkAssign = async () => {
		if (!bulkRoleId || !bulkFormId || !cycleId) {
			toast.showToast('Please select both role and form for bulk assignment', 'error')
			return
		}

		await bulkAssignAllMutation.mutateAsync({
			cycleId,
			roleId: bulkRoleId,
			formId: bulkFormId,
		})
	}

	const handleAssignRoles = async () => {
		if (!cycleId || !participantsRolesToMap.length) return

		const invalidMappings = participantsRolesToMap.filter((p) => !p.roleId || !p.formId)
		if (invalidMappings.length > 0) {
			toast.showToast('Please select both role and form for all participants', 'error')
			return
		}

		const roles = participantsRolesToMap.map((participant) => ({
			userId: participant.id,
			roleId: participant.roleId,
			formId: participant.formId,
		}))

		if (roles.length === 0) return

		await assignRolesToParticipantsMutation.mutateAsync({
			cycleId,
			participants: roles as [(typeof roles)[0], ...typeof roles],
		})
	}

	return (
		<div>
			<GenericTable<CycleRolesUnmapped, CycleRolesUnmappedInput>
				columns={columns}
				idField='id'
				queryOptionsGenerator={(options) => ({
					...trpc.admin.cycles.roles.participants.listUnassigned.queryOptions({
						...options,
						cycleId: cycleId as string,
					}),
				})}
				isInternal
				showSearch
				showPagination
				actionButtons={[
					{
						children: 'Assign All',
						onClick: () => setIsBulkAssignModalOpen(true),
						variant: 'secondary' as const,
					},
				]}
				emptyState={{
					icon: RolesIcon,
					title: 'Unmapped Roles',
					description: 'There are no participants with unmapped roles',
				}}
			/>
			{participantsRolesToMap.length !== 0 && currentStep === 2 && (
				<ActionBar
					rightActions={[
						{
							variant: 'primary',
							label: 'Save and continue',
							onClick: () => {
								markStepCompleted(2)
								handleAssignRoles()
							},
						},
					]}
				/>
			)}

			<Modal
				isOpen={isBulkAssignModalOpen}
				onClose={() => {
					setIsBulkAssignModalOpen(false)
					setBulkRoleId('')
					setBulkFormId('')
				}}
				title='Assign Role and Form to All Unmapped Roles'>
				<div className='p-6 space-y-6 pb-24'>
					<div>
						<span className='block mb-2'>
							<Text variant='small' weight='semibold'>
								Select Role
							</Text>
						</span>
						<Select
							value={bulkRoleId}
							placeholder='Select role'
							options={
								roles?.data
									?.filter((value) => value.roleId !== null)
									.map((value) => ({
										label: value.name,
										value: value.roleId as string,
									})) ?? []
							}
							onChange={(value) => {
								setBulkRoleId(value)
								const autoFormId = roles?.data?.find((r) => r.roleId === value)?.formId ?? ''
								setBulkFormId(autoFormId)
							}}
						/>
					</div>

					<div>
						<span className='block mb-2'>
							<Text variant='small' weight='semibold'>
								Select Form
							</Text>
						</span>
						<Select
							value={bulkFormId}
							placeholder='Select form'
							options={formOptions}
							onChange={setBulkFormId}
							onMenuScrollToBottom={() => {
								if (hasNextPage && !isFetchingNextPage) {
									fetchNextPage()
								}
							}}
							isLoadingMore={isFetchingNextPage}
							hasMore={hasNextPage}
						/>
					</div>

					<Text variant='small' className='text-neutral-600'>
						This will assign the selected role and form to all unmapped participants in this cycle.
					</Text>
				</div>

				<ActionBar
					variant='modal'
					rightActions={[
						{
							variant: 'secondary',
							label: 'Cancel',
							onClick: () => {
								setIsBulkAssignModalOpen(false)
								setBulkRoleId('')
								setBulkFormId('')
							},
						},
						{
							variant: 'primary',
							label: 'Assign to All',
							onClick: handleBulkAssign,
							disabled: !bulkRoleId || !bulkFormId,
						},
					]}
				/>
			</Modal>
		</div>
	)
}

export default CycleUnmappedRoles
